"use client";

import { useState } from "react";
import {
  ArrowUpRight,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  Mail,
} from "lucide-react";

import { Button } from "@/components/ui/button";

type ActionResult = {
  ok: boolean;
  message: string;
  redirectTo?: string;
};

export function PortalLoginForm({
  loginAction,
}: {
  loginAction: (formData: FormData) => Promise<ActionResult>;
}) {
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<ActionResult | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (loading) return;

    setLoading(true);
    setNotice(null);

    const formData = new FormData(event.currentTarget);

    try {
      const result = await loginAction(formData);
      setNotice(result);

      if (result.ok && result.redirectTo) {
        window.location.href = result.redirectTo;
      }
    } catch (error) {
      setNotice({
        ok: false,
        message:
          error instanceof Error
            ? error.message
            : "Could not login. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-7">
      <form onSubmit={handleSubmit} className="space-y-4">
        {notice && (
          <div
            className={[
              "rounded-[16px] border px-4 py-3 text-[13px] font-semibold",
              notice.ok
                ? "border-[#cdeed6] bg-[#eaf8ee] text-[#248a3d]"
                : "border-[#ffd1d1] bg-[#fff0f0] text-[#d70015]",
            ].join(" ")}
          >
            {notice.message}
          </div>
        )}

        <Field label="Email address" icon={<Mail className="size-4" />}>
          <input
            name="email"
            type="email"
            required
            disabled={loading}
            autoComplete="email"
            placeholder="email@company.com"
            className="h-12 w-full rounded-[18px] border border-[#e5e5ea] bg-[#fbfbfd] pr-11 pl-4 text-[14px] font-medium text-[#1d1d1f] outline-none transition placeholder:text-[#a1a1a6] focus:border-[#5f5144]/45 focus:bg-white disabled:opacity-70"
          />
        </Field>

        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="text-[13px] font-semibold text-[#1d1d1f]">
              Password
            </label>

            <a
              href="mailto:info@atyafco.com?subject=Reset%20company%20portal%20password"
              className="text-[12px] font-semibold text-[#5f5144] transition hover:text-[#2b211b] hover:underline"
            >
              Forgot password?
            </a>
          </div>

          <div className="relative">
            <LockKeyhole className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-[#86868b]" />

            <input
              name="password"
              type={showPassword ? "text" : "password"}
              required
              disabled={loading}
              autoComplete="current-password"
              placeholder="Enter password"
              className="h-12 w-full rounded-[18px] border border-[#e5e5ea] bg-[#fbfbfd] pr-11 pl-12 text-[14px] font-medium text-[#1d1d1f] outline-none transition placeholder:text-[#a1a1a6] focus:border-[#5f5144]/45 focus:bg-white disabled:opacity-70"
            />

            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              disabled={loading}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#86868b] transition hover:text-[#1d1d1f] disabled:opacity-50"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="h-12 w-full rounded-[18px] bg-[#2b211b] text-[14px] font-semibold text-white shadow-none transition hover:bg-[#3a2c24] disabled:pointer-events-none disabled:opacity-60"
        >
          {loading && <Loader2 className="ml-2 size-4 animate-spin" />}
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>

      <div className="mt-5 rounded-[18px] border border-[#e5e5ea] bg-[#fbfbfd] px-4 py-3">
        <p className="text-center text-[12px] leading-5 text-[#86868b]">
          Need help accessing your account?{" "}
          <a
            href="mailto:info@atyafco.com?subject=Company%20portal%20access%20support"
            className="font-semibold text-[#2b211b] hover:underline"
          >
            info@atyafco.com
          </a>
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-[13px] font-semibold text-[#1d1d1f]">
        {label}
      </span>

      <div className="relative">
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#86868b]">
          {icon}
        </span>

        {children}
      </div>
    </label>
  );
}
